import { IconFavorite, IconFavoriteFilled } from '@tvg/fuse-icons';
import { SrOnly } from '@tvg/fuse-utils';
import PropTypes from 'prop-types';
import React from 'react';
import { Icon } from '../../fuse-icon-element/src/icon-element';
import { componentConfig } from './config';
import { StyledSave, StyledSaveLabel } from './styled-save';

const { forwardRef, useCallback, useState, useEffect } = React;

const propTypes = {
	clickCallback: PropTypes.func,
	dataTestId: PropTypes.string,
	saveLabel: PropTypes.string,
	savedLabel: PropTypes.string,
	showLabel: PropTypes.bool,
	isSaved: PropTypes.bool,
	iconSize: PropTypes.oneOfType([
		PropTypes.oneOf(componentConfig.icon.sizes),
		PropTypes.shape(
			['xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl'].reduce(
				(acc, current) => ({ ...acc, [current]: PropTypes.oneOf(componentConfig.icon.sizes) }),
				{},
			),
		),
	]),
};

const defaultProps = {
	clickCallback: null,
	dataTestId: null,
	saveLabel: null,
	savedLabel: null,
	showLabel: true,
	isSaved: false,
	iconSize: componentConfig.icon.sizes[1], // medium
};

const Save = forwardRef(
	(
		{ clickCallback, dataTestId, saveLabel, savedLabel, showLabel, isSaved, iconSize, ...others },
		ref,
	) => {
		// Component config
		const { icon, label } = componentConfig;
		// States
		const [saved, setSaved] = useState(isSaved);
		const [focused, setFocused] = useState(false);
		const [messageSR, setMessageSR] = useState(false);
		// Set default label and srLabel texts based on saved state
		const saveLabelText = !saved && (saveLabel || 'Save for later');
		const savedLabelText = saved && (savedLabel || 'Saved');
		const labelText = saved ? savedLabelText : saveLabelText;
		// Set srLabel to labelText only when showLabel === false
		const srLabel = !showLabel ? labelText : '';
		const savedIconClassName = saved && icon.savedIconClassName;

		// Event handlers
		const saveClickHandler = useCallback(
			(e) => {
				// Run callback
				if (typeof clickCallback === 'function') clickCallback(e, ref, !saved);
				// Toggle between save / saved states
				setSaved((toggle) => !toggle);
			},
			[saved, clickCallback],
		);
		const focusHandler = useCallback(() => setFocused(true), []);
		const blurHandler = useCallback(() => setFocused(false), []);

		useEffect(() => {
			if (focused) {
				setMessageSR(labelText);
			}
		}, [labelText]);

		useEffect(() => {
			if (!focused) {
				setMessageSR('');
			}
		}, [focused]);

		useEffect(() => {
			setSaved(isSaved);
		}, [isSaved]);

		return (
			<>
				<StyledSave
					data-testid={dataTestId}
					onClick={saveClickHandler}
					aria-pressed={saved}
					ref={ref}
					onFocus={focusHandler}
					onBlur={blurHandler}
					{...others}
				>
					<Icon
						icon={saved ? <IconFavoriteFilled /> : <IconFavorite />}
						className={savedIconClassName}
						color={saved ? 'var(--color-brand-mid)' : 'var(--color-global-black)'}
						responsiveSize={iconSize}
						srLabel={srLabel}
					/>
					{showLabel && (
						<StyledSaveLabel
							color={label.color}
							size={label.size}
							tag={label.tag}
							type={label.type}
						>
							{labelText}
						</StyledSaveLabel>
					)}
				</StyledSave>
				{focused && <SrOnly aria-live="polite">{messageSR}</SrOnly>}
			</>
		);
	},
);

Save.propTypes = propTypes;
Save.defaultProps = defaultProps;

export { Save };

export default Save;
